import {
	useLayoutEffect,
	useState,
	type RefObject,
} from "react";

const DEFAULT_MINIMUM_LINE_COUNT = 1;
const DEFAULT_LINE_HEIGHT = 20;

const getPixelValue = (
	value: string | null | undefined
): number => {
	if (!value) {
		return 0;
	}

	const parsedValue = Number.parseFloat(value);
	return Number.isFinite(parsedValue) ? parsedValue : 0;
};

export const useMinimumEditorLineCount = (
	options: {
		shellRef: RefObject<HTMLDivElement | null>;
		enabled: boolean;
		fallbackMinimumLineCount: number;
		height?: string;
		minHeight?: string;
	}
): number => {
	const {
		shellRef,
		enabled,
		fallbackMinimumLineCount,
		height,
		minHeight,
	} = options;
	const [minimumLineCount, setMinimumLineCount] = useState(
		enabled
			? Math.max(fallbackMinimumLineCount, 1)
			: DEFAULT_MINIMUM_LINE_COUNT
	);

	useLayoutEffect(() => {
		if (!enabled) {
			setMinimumLineCount(DEFAULT_MINIMUM_LINE_COUNT);
			return;
		}

		const shell = shellRef.current;
		if (!shell) {
			return;
		}

		const updateMinimumLineCount = () => {
			const scroller =
				shell.querySelector<HTMLElement>(".cm-scroller");
			const editor =
				shell.querySelector<HTMLElement>(".cm-editor");
			const content =
				shell.querySelector<HTMLElement>(".cm-content");
			const line =
				shell.querySelector<HTMLElement>(".cm-line");

			const heightTarget = scroller ?? editor ?? shell;
			const configuredHeight =
				getPixelValue(height) || getPixelValue(minHeight);
			const measuredHeight =
				heightTarget.getBoundingClientRect().height;
			const targetHeight =
				configuredHeight || measuredHeight;

			if (targetHeight <= 0) {
				return;
			}

			const lineHeightSource = line ?? content ?? heightTarget;
			const lineHeightStyle = window.getComputedStyle(
				lineHeightSource
			);
			const measuredLineHeight =
				line?.getBoundingClientRect().height ?? 0;
			const computedLineHeight = getPixelValue(
				lineHeightStyle.lineHeight
			);
			const lineHeight =
				measuredLineHeight ||
				computedLineHeight ||
				DEFAULT_LINE_HEIGHT;

			const contentStyle = content
				? window.getComputedStyle(content)
				: null;
			const verticalPadding =
				getPixelValue(contentStyle?.paddingTop) +
				getPixelValue(contentStyle?.paddingBottom);
			const availableHeight = Math.max(
				targetHeight - verticalPadding,
				lineHeight
			);
			const nextMinimumLineCount = Math.max(
				Math.floor(availableHeight / lineHeight),
				fallbackMinimumLineCount,
				1
			);

			setMinimumLineCount((previousMinimumLineCount) => {
				if (
					previousMinimumLineCount ===
					nextMinimumLineCount
				) {
					return previousMinimumLineCount;
				}

				return nextMinimumLineCount;
			});
		};

		const frameId = window.requestAnimationFrame(
			updateMinimumLineCount
		);
		const resizeObserver =
			typeof ResizeObserver !== "undefined"
				? new ResizeObserver(() => {
						updateMinimumLineCount();
					})
				: null;

		resizeObserver?.observe(shell);
		const scroller = shell.querySelector(".cm-scroller");
		if (scroller instanceof HTMLElement) {
			resizeObserver?.observe(scroller);
		}
		const editor = shell.querySelector(".cm-editor");
		if (editor instanceof HTMLElement) {
			resizeObserver?.observe(editor);
		}

		return () => {
			window.cancelAnimationFrame(frameId);
			resizeObserver?.disconnect();
		};
	}, [
		enabled,
		fallbackMinimumLineCount,
		height,
		minHeight,
		shellRef,
	]);

	return minimumLineCount;
};
